import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import type { ReversalPayrollState, RevokeRegularisationPreview } from "./reversal";

export type CorrectionType = "in_only" | "out_only" | "both";

export type RegularisationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled"
  | "revoked";

export type RegularisationReasonType =
  | "whatsapp_location"
  | "forgot_mark_in"
  | "forgot_mark_out"
  | "forgot_attendance"
  | "system_issue"
  | "client_visit"
  | "official_travel"
  | "others";

export interface RegularisationRow {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  attendanceId: string | null;
  attendanceDate: string | null;
  correctionType: CorrectionType;
  correctedInTime: string | null;
  correctedOutTime: string | null;
  inLocationLabel: string;
  outLocationLabel: string;
  reasonType: RegularisationReasonType;
  reasonDetails: string;
  attachmentId: string | null;
  attachmentFilename: string | null;
  attachmentContentType: string | null;
  status: RegularisationStatus;
  submittedAt: string | null;
  submittedById: string | null;
  submittedByName: string | null;
  isProxy: boolean;
  bypassReason: string;
  windowId: string | null;
  windowLabel: string | null;
  reviewedAt: string | null;
  reviewedById: string | null;
  reviewedByName: string | null;
  reviewNote: string;
  cancelReason: string;
  revokedAt: string | null;
  revokedById: string | null;
  revokedByName: string | null;
  revocationReason: string;
  canRevoke: boolean;
  createdAt: string | null;
}

export interface ProxyTarget {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface RegularisationListResponse {
  results: RegularisationRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

export interface RegularisationCreateInput {
  attendanceDate: string;
  correctionType: CorrectionType;
  correctedInTime?: string | null;
  correctedOutTime?: string | null;
  inLocationLabel?: string;
  outLocationLabel?: string;
  reasonType: RegularisationReasonType;
  reasonDetails: string;
  attachmentId?: string | null;
  // Proxy filing (Technical Admin only). Setting ``targetUserId`` routes the
  // request to ``submit_proxy_regularisation`` server-side, bypassing the
  // 3-day window.
  targetUserId?: string | null;
  bypassReason?: string;
}

const listKey = (params: object) => ["regularisations", "list", params] as const;

export function useRegularisations(
  params: {
    scope?: "self" | "all" | "submitted_by_self";
    status?: RegularisationStatus | "";
    userId?: string;
    from?: string;
    to?: string;
  } = {},
  enabled: boolean = true,
) {
  return useQuery<RegularisationListResponse>({
    queryKey: listKey(params),
    queryFn: () =>
      api
        .get<RegularisationListResponse>("/attendance-regularisations", {
          params: {
            scope: params.scope || "self",
            status: params.status || undefined,
            userId: params.userId || undefined,
            from: params.from || undefined,
            to: params.to || undefined,
          },
        })
        .then((r) => r.data),
    enabled,
  });
}

export function useSubmitRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RegularisationCreateInput) =>
      api
        .post<RegularisationRow>("/attendance-regularisations", input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regularisations"] }),
  });
}

export function useProxyTargets(search: string, enabled: boolean = true) {
  return useQuery<{ results: ProxyTarget[] }>({
    queryKey: ["regularisations", "proxy-targets", search],
    queryFn: () =>
      api
        .get<{ results: ProxyTarget[] }>(
          "/attendance-regularisations/proxy-targets",
          { params: { search: search || undefined } },
        )
        .then((r) => r.data),
    enabled,
  });
}

export function useCancelRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api
        .post<RegularisationRow>(`/attendance-regularisations/${id}/cancel`, {
          reason: reason ?? "",
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regularisations"] }),
  });
}

/* ------------------------------- reversal (SA/TA) ------------------------------ */

/** Dry run — what revoking this regularisation would do. No writes. */
export function useRevokeRegularisationPreview(id: string | null, enabled = true) {
  return useQuery<RevokeRegularisationPreview>({
    queryKey: ["regularisations", "revokePreview", id],
    queryFn: () =>
      api
        .get<RevokeRegularisationPreview>(`/attendance-regularisations/${id}/revoke`)
        .then((r) => r.data),
    enabled: enabled && !!id,
  });
}

export function useRevokeRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api
        .post<RegularisationRow>(`/attendance-regularisations/${id}/revoke`, { reason })
        .then((r) => r.data),
    onSuccess: () => {
      // Reversal rewrites attendance and can restore leave windows — refresh
      // every surface that reads those, not just the regularisation list.
      qc.invalidateQueries({ queryKey: ["regularisations"] });
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["leaves"] });
    },
  });
}

/* --------------------------- regularisation windows --------------------------- */

export type WindowScope = "org" | "department" | "employee";

export interface RegularisationWindowRow {
  id: string;
  scope: WindowScope;
  departmentId: string | null;
  departmentName: string | null;
  users: { id: string; name: string }[];
  usersLabel: string | null;
  periodFrom: string;
  periodTo: string;
  monthsLabel: string;
  autoCloseOn: string | null;
  note: string;
  status: "active" | "inactive";
  openedById: string | null;
  openedByName: string | null;
  closedByName: string | null;
  closedAt: string | null;
  requestCount: number | null;
  createdAt: string | null;
}

export interface WindowCreateInput {
  scope: WindowScope;
  departmentId?: string | null;
  userIds?: string[];
  periodFrom: string; // YYYY-MM-01
  periodTo: string;
  autoCloseOn?: string | null;
  note?: string;
}

export function useRegularisationWindows(enabled = true) {
  return useQuery<RegularisationWindowRow[]>({
    queryKey: ["regularisationWindows", "list"],
    queryFn: () =>
      api
        .get<RegularisationWindowRow[]>("/attendance-regularisations/windows")
        .then((r) => r.data),
    enabled,
  });
}

/** Active windows covering ME — employee banner + create-drawer date bounds. */
export function useMyRegularisationWindows(enabled = true) {
  return useQuery<RegularisationWindowRow[]>({
    queryKey: ["regularisationWindows", "mine"],
    queryFn: () =>
      api
        .get<RegularisationWindowRow[]>("/attendance-regularisations/windows/mine")
        .then((r) => r.data),
    enabled,
  });
}

export function useOpenRegularisationWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WindowCreateInput) =>
      api
        .post<RegularisationWindowRow>("/attendance-regularisations/windows", input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regularisationWindows"] }),
  });
}

export function useCloseRegularisationWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (windowId: string) =>
      api
        .post<RegularisationWindowRow>(
          `/attendance-regularisations/windows/${windowId}/close`,
          {},
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regularisationWindows"] }),
  });
}

/* ---------------------------- approval preview --------------------------- */

export type ApprovalNoteLevel = "info" | "warning" | "danger";

/** One line of "here is what approving does". Composed server-side so the
 *  dialog never re-derives a domain rule the services own. */
export interface ApprovalNote {
  level: ApprovalNoteLevel;
  code: string;
  message: string;
}

export interface ApprovalLeaveEffect {
  leaveId: string;
  leaveType: string | null;
  durationKind: "full_day" | "half_day" | "partial";
  status: string;
  startsOn: string;
  endsOn: string;
  totalDays: number;
  action: "rejected" | "shrunk" | "noop";
  /** "full_day" | "half_day" | "covered" | "split" | "degenerate" | "" */
  reason: string;
  priorWindow: { start: string | null; end: string | null } | null;
  newWindow: { start: string | null; end: string | null } | null;
  /** Days the leave covers that this regularisation does NOT touch — they lose
   *  leave anyway, because the whole request is rejected. */
  collateralDates: string[];
  collateralDayCount: number;
}

export interface ApprovalPreview {
  regularisationId: string;
  userId: string;
  userName: string | null;
  attendanceDate: string;
  correctionType: CorrectionType;
  status: RegularisationStatus;
  isProxy: boolean;
  attendance: {
    action: "create" | "update";
    id: string | null;
    currentMarkedInAt: string | null;
    currentMarkedOutAt: string | null;
    resultMarkedInAt: string | null;
    resultMarkedOutAt: string | null;
    rewrites: string[];
  };
  leaveEffects: ApprovalLeaveEffect[];
  warnings: ApprovalNote[];
  blockers: ApprovalNote[];
  canApprove: boolean;
  payroll: ReversalPayrollState;
}

/** Dry run — what approving this request would do. No writes. */
export function useApprovalPreview(id: string | null, enabled = true) {
  return useQuery<ApprovalPreview>({
    queryKey: ["regularisations", "approvalPreview", id],
    queryFn: () =>
      api
        .get<ApprovalPreview>(`/attendance-regularisations/${id}/review`)
        .then((r) => r.data),
    enabled: enabled && !!id,
    // Attendance and leaves move underneath a queue that sits open all day.
    staleTime: 0,
  });
}

export function useReviewRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string;
      decision: "approved" | "rejected";
      note?: string;
    }) =>
      api
        .post<RegularisationRow>(`/attendance-regularisations/${id}/review`, {
          decision,
          note: note ?? "",
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["regularisations"] });
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["leaves"] });
    },
  });
}
